import { ACL_TYPE } from '@openx/types/aclType';
import { CuratedPackageAction, type PackageListItem } from '@openx/types/package';
import { type User, UserRole } from '@openx/types/user';
import type { IsAllowedCheck } from '@openx/utils/permission/useIsAllowed';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators';
import { useCallback } from 'react';
import { usePackageContext } from '../context/PackageContext';

export interface PackageActionPermissions {
  canEdit: (pckg: PackageListItem) => boolean;
  canDelete: (pckg: PackageListItem) => boolean;
  canRead: (pckg: PackageListItem) => boolean;
  canCreate: () => boolean;
  canClone: (pckg: PackageListItem) => boolean;
}

export interface UsePackageActionPermissionsParams {
  isAllowed: IsAllowedCheck;
  accessUser?: User;
}

export const usePackageActionPermissions = ({
  isAllowed,
  accessUser,
}: UsePackageActionPermissionsParams): PackageActionPermissions => {
  const { usePackagePermissions } = usePackageContext();
  const { hasCuratedPermission } = usePackagePermissions();

  const canEdit = useCallback(
    (pckg: PackageListItem): boolean => {
      const hasAclPermission = isAllowed(isAllowedByCompiledAcl(ACL_TYPE.PACKAGE_UPDATE));

      const hasCuratedAccess = hasCuratedPermission({
        action: CuratedPackageAction.UPDATE,
        pckg,
      });

      const hasInternalFieldAccess = isAllowed(isAllowedByCompiledAcl(ACL_TYPE.INTERNAL_FIELD_WRITE));
      const hasOpenxBuyerRole = accessUser?.roles?.includes(UserRole.NETWORK_OA_OPENX_BUYER) ?? false;
      const isBlockedByLock = pckg?.locked_internal && !hasInternalFieldAccess && !hasOpenxBuyerRole;

      return hasAclPermission && hasCuratedAccess && !isBlockedByLock;
    },
    [isAllowed, hasCuratedPermission, accessUser?.roles]
  );

  const canDelete = useCallback(
    (pckg: PackageListItem): boolean => {
      const hasAclPermission = isAllowed(isAllowedByCompiledAcl(ACL_TYPE.PACKAGE_DELETE));

      const hasCuratedAccess = hasCuratedPermission({
        action: CuratedPackageAction.DELETE,
        pckg,
      });

      const hasInternalFieldAccess = isAllowed(isAllowedByCompiledAcl(ACL_TYPE.INTERNAL_FIELD_WRITE));
      const hasOpenxBuyerRole = accessUser?.roles?.includes(UserRole.NETWORK_OA_OPENX_BUYER) ?? false;
      const isBlockedByLock = pckg?.locked_internal && !hasInternalFieldAccess && !hasOpenxBuyerRole;

      return hasAclPermission && hasCuratedAccess && !isBlockedByLock;
    },
    [isAllowed, hasCuratedPermission, accessUser?.roles]
  );

  const canRead = useCallback(
    (_pckg: PackageListItem): boolean => {
      return isAllowed(isAllowedByCompiledAcl(ACL_TYPE.PACKAGE_READ));
    },
    [isAllowed]
  );

  const canClone = useCallback(
    (pckg: PackageListItem): boolean => {
      const hasCreatePermission = isAllowed(isAllowedByCompiledAcl(ACL_TYPE.PACKAGE_CREATE));

      if (pckg?.locked_internal) {
        const hasInternalFieldAccess = isAllowed(isAllowedByCompiledAcl(ACL_TYPE.INTERNAL_FIELD_WRITE));
        const hasOpenxBuyerRole = accessUser?.roles?.includes(UserRole.NETWORK_OA_OPENX_BUYER) ?? false;

        return (hasInternalFieldAccess || hasOpenxBuyerRole) && hasCreatePermission;
      }

      return hasCreatePermission;
    },
    [isAllowed, accessUser?.roles]
  );

  const canCreate = useCallback((): boolean => {
    return isAllowed(isAllowedByCompiledAcl(ACL_TYPE.PACKAGE_CREATE));
  }, [isAllowed]);

  return {
    canEdit,
    canDelete,
    canRead,
    canCreate,
    canClone,
  };
};
